import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, TrendingUp, TrendingDown, X, DollarSign, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const ExchangeDashboard = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Properly convert HTTP to WS URL
  const wsUrl = backendUrl.replace(/^http/, 'ws');

  const connectWebSocket = () => {
    try {
      const fullWsUrl = `${wsUrl}/ws/exchanges/dashboard`;
      console.log('Connecting to WebSocket:', fullWsUrl);
      const ws = new WebSocket(fullWsUrl);
      
      // Timeout for connection
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.warn('WebSocket connection timeout, falling back to polling');
          ws.close();
          setWsConnected(false);
          // Start polling as fallback
          startPolling();
        }
      }, 3000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected');
        setWsConnected(true);
        // Send auth message with user_id
        ws.send(JSON.stringify({ user_id: user.id }));
        
        // Send ping every 30 seconds to keep connection alive
        const pingInterval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          } else {
            clearInterval(pingInterval);
          }
        }, 30000);
      };
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.type === 'dashboard_update') {
          setDashboardData(data.exchanges || []);
          setLastUpdate(new Date());
          console.log('Dashboard updated via WebSocket');
        }
      };
      
      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error:', error);
        console.log('WebSocket details:', {
          url: fullWsUrl,
          readyState: ws.readyState,
          protocol: ws.protocol
        });
        setWsConnected(false);
      };
      
      ws.onclose = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket disconnected');
        setWsConnected(false);
        // Try to reconnect after 5 seconds if user still active
        setTimeout(() => {
          if (user && !wsRef.current) {
            console.log('Attempting to reconnect WebSocket...');
            connectWebSocket();
          }
        }, 5000);
      };
      
      wsRef.current = ws;
    } catch (error) {
      console.error('Error connecting to WebSocket:', error);
      setWsConnected(false);
      // Fall back to polling
      startPolling();
    }
  };

  const startPolling = () => {
    console.log('Starting polling mode (fallback)');
    const interval = setInterval(() => {
      if (user) {
        fetchDashboard(true);
      } else {
        clearInterval(interval);
      }
    }, 2000); // Poll every 2 seconds
  };
  useEffect(() => {
    if (!loading && !user) {
      navigate('/');
    } else if (user) {
      // Initial fetch
      fetchDashboard();
      
      // Connect to WebSocket for real-time updates
      connectWebSocket();
      
      return () => {
        if (wsRef.current) {
          wsRef.current.close();
        }
      };
    }
  }, [user, loading, navigate]);

  const fetchDashboard = async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const response = await axios.get(`${backendUrl}/api/exchanges/dashboard`, {
        withCredentials: true
      });
      setDashboardData(response.data.exchanges || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      if (!silent) {
        toast.error('Ошибка загрузки данных');
      }
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const handleCancelOrder = async (exchangeName, orderId, symbol) => {
    if (!window.confirm('Отменить этот ордер?')) return;
    
    try {
      await axios.delete(
        `${backendUrl}/api/exchanges/orders/${orderId}?exchange_name=${exchangeName}&symbol=${symbol}`,
        { withCredentials: true }
      );
      toast.success('Ордер отменён');
      fetchDashboard();
    } catch (error) {
      toast.error('Ошибка отмены ордера');
    }
  };

  const handleClosePosition = async (exchangeName, symbol) => {
    if (!window.confirm(`Закрыть позицию ${symbol}?`)) return;
    
    try {
      await axios.post(
        `${backendUrl}/api/exchanges/positions/close`,
        { exchange_name: exchangeName, symbol },
        { withCredentials: true }
      );
      toast.success('Позиция закрыта');
      fetchDashboard();
    } catch (error) {
      toast.error('Ошибка закрытия позиции');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Toaster position="top-right" />
      <Header />
      
      <div className="pt-20 pb-8 px-4 md:px-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 flex items-center gap-3">
              Дашборд бирж
              {wsConnected ? (
                <Badge variant="outline" className="bg-green-500/20 text-green-400 border-green-500/50">
                  <Wifi className="h-3 w-3 mr-1" />
                  Live
                </Badge>
              ) : (
                <Badge variant="outline" className="bg-gray-500/20 text-gray-400 border-gray-500/50">
                  <WifiOff className="h-3 w-3 mr-1" />
                  Offline
                </Badge>
              )}
            </h1>
            <p className="text-gray-400">
              Мониторинг балансов, позиций и ордеров в реальном времени
            </p>
            {lastUpdate && (
              <p className="text-xs text-gray-500 mt-1">
                Последнее обновление: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>
          <Button 
            onClick={() => fetchDashboard()} 
            disabled={refreshing}
            className="bg-[#00ff88] text-black hover:opacity-90"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </div>

        {/* Dashboard Cards */}
        {dashboardData.length === 0 ? (
          <Card className="bg-[#1a1a1a] border-gray-800">
            <CardContent className="p-6 text-center text-gray-400">
              Нет подключенных бирж. Подключите биржу в настройках.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {dashboardData.map((exchange) => (
              <Card key={`${exchange.exchange_name}_${exchange.connection_id}`} className="bg-[#1a1a1a] border-gray-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{exchange.exchange_name.toUpperCase()}</span>
                      <Badge variant="outline" className="bg-purple-500/20 text-purple-400 border-purple-500/50">
                        {exchange.key_name || 'Ключ 1'}
                      </Badge>
                      {exchange.testnet && (
                        <span className="text-xs px-2 py-1 rounded bg-yellow-900/30 text-yellow-400">
                          TESTNET
                        </span>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {exchange.error ? (
                    <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
                      <p className="text-red-400 font-semibold mb-2">⚠️ Ошибка подключения</p>
                      <p className="text-red-300 text-sm mb-3">{exchange.error}</p>
                      {exchange.error.includes('decryption') || exchange.error.includes('reconnect') ? (
                        <Button
                          size="sm"
                          onClick={() => navigate('/exchanges')}
                          className="bg-red-600 hover:bg-red-700 text-white"
                        >
                          Переподключить биржу
                        </Button>
                      ) : null}
                    </div>
                  ) : (
                    <>
                      {/* Balance */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-400 mb-1">Общий баланс</p>
                          <p className="text-2xl font-bold text-white">
                            ${exchange.balance?.total?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-400 mb-1">Доступно</p>
                          <p className="text-2xl font-bold text-green-400">
                            ${exchange.balance?.free?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                        <div className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                          <p className="text-sm text-gray-400 mb-1">В ордерах</p>
                          <p className="text-2xl font-bold text-yellow-400">
                            ${exchange.balance?.used?.toFixed(2) || '0.00'}
                          </p>
                        </div>
                      </div>

                      {/* Open Positions */}
                      {exchange.positions && exchange.positions.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-3">
                            Открытые позиции ({exchange.positions.length})
                          </h3>
                          <div className="space-y-2">
                            {exchange.positions.map((position, idx) => (
                              <div key={idx} className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-white font-semibold">{position.symbol}</span>
                                      <span className={`text-xs px-2 py-1 rounded ${
                                        position.side === 'long' 
                                          ? 'bg-green-900/30 text-green-400' 
                                          : 'bg-red-900/30 text-red-400'
                                      }`}>
                                        {position.side === 'long' ? (
                                          <><TrendingUp className="h-3 w-3 inline mr-1" />LONG</>
                                        ) : (
                                          <><TrendingDown className="h-3 w-3 inline mr-1" />SHORT</>
                                        )}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <p className="text-gray-500">Размер</p>
                                        <p className="text-white">{position.contracts}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Цена входа</p>
                                        <p className="text-white">${position.entry_price?.toFixed(2)}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">PnL</p>
                                        <p className={position.unrealized_pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
                                          ${position.unrealized_pnl?.toFixed(2)} ({position.percentage?.toFixed(2)}%)
                                        </p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Стоимость</p>
                                        <p className="text-white">${position.notional?.toFixed(2)}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleClosePosition(exchange.exchange_name, position.symbol)}
                                    className="border-red-900 text-red-400 hover:bg-red-900/20"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Закрыть
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Open Orders */}
                      {exchange.orders && exchange.orders.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-3">
                            Активные ордера ({exchange.orders.length})
                          </h3>
                          <div className="space-y-2">
                            {exchange.orders.map((order) => (
                              <div key={order.id} className="bg-[#0a0a0a] border border-gray-800 rounded-lg p-4">
                                {/* Setup Info if available */}
                                {order.setup_title && (
                                  <div className="mb-3 pb-3 border-b border-gray-800">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs text-gray-400">Сетап:</span>
                                      <span className="text-sm text-[#00ff88] font-semibold">{order.setup_title}</span>
                                      <Badge className="bg-blue-500/20 text-blue-400 text-xs">
                                        Шаг {order.step_number}/{order.total_steps}
                                      </Badge>
                                      {order.order_type && (
                                        <Badge className={`text-xs ${
                                          order.order_type === 'entry' 
                                            ? 'bg-yellow-500/20 text-yellow-400' 
                                            : 'bg-green-500/20 text-green-400'
                                        }`}>
                                          {order.order_type === 'entry' ? 'Entry' : 'Take Profit'}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}
                                
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                      <span className="text-white font-semibold">{order.symbol}</span>
                                      <span className={`text-xs px-2 py-1 rounded ${
                                        order.side === 'buy' 
                                          ? 'bg-green-900/30 text-green-400' 
                                          : 'bg-red-900/30 text-red-400'
                                      }`}>
                                        {order.side.toUpperCase()}
                                      </span>
                                      <span className="text-xs px-2 py-1 rounded bg-blue-900/30 text-blue-400">
                                        {order.type.toUpperCase()}
                                      </span>
                                    </div>
                                    <div className="grid grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <p className="text-gray-500">Цена</p>
                                        <p className="text-white">${order.price?.toFixed(2) || 'Market'}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Количество</p>
                                        <p className="text-white">{order.amount}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Исполнено</p>
                                        <p className="text-white">{order.filled} / {order.amount}</p>
                                      </div>
                                      <div>
                                        <p className="text-gray-500">Статус</p>
                                        <p className="text-yellow-400">{order.status}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleCancelOrder(exchange.exchange_name, order.id, order.symbol)}
                                    className="border-red-900 text-red-400 hover:bg-red-900/20"
                                  >
                                    <X className="h-4 w-4 mr-1" />
                                    Отменить
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* No positions/orders message */}
                      {(!exchange.positions || exchange.positions.length === 0) && 
                       (!exchange.orders || exchange.orders.length === 0) && (
                        <div className="text-center py-8 text-gray-400">
                          Нет открытых позиций или ордеров
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExchangeDashboard;
